import React, { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ColoradoFormPdfEditor from '../components/transaction/ColoradoFormPdfEditor';

/**
 * Standalone, full-screen host for ColoradoFormPdfEditor — used ONLY when embedded inside
 * the mobile app's WebView (see Mob's ColoradoFormEditScreen.tsx), which has no equivalent
 * of the web editor's canvas/pdf.js overlay trick and instead reuses this exact component.
 *
 * Not linked from anywhere in the normal web app UI. Auth comes from a token the RN host
 * injects onto `window` before this page's own JS runs (WebView
 * injectedJavaScriptBeforeContentLoaded) — NOT the normal Firebase session — so this route
 * is intentionally NOT wrapped in <RequireAuth> in App.js.
 */
export default function EmbedFormEditPage() {
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get('packageId');
  const instanceId = searchParams.get('instanceId');
  const name = searchParams.get('name') || 'Form';
  const closedRef = useRef(false);

  const getToken = useCallback(async () => window.__RIALTY_TOKEN__ || '', []);

  // Only the first call wins — ColoradoFormPdfEditor's handleSave fires
  // onSaved() immediately followed by onClose(); we want the RN host to see
  // exactly one message, and saved=true should win over the close that follows it.
  const notifyClosed = useCallback((saved) => {
    if (closedRef.current) return;
    closedRef.current = true;
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'FORM_EDIT_CLOSED', saved }));
    }
  }, []);

  if (!packageId || !instanceId) {
    return (
      <div style={{ padding: 24, fontFamily: 'sans-serif', color: '#2D1B69' }}>
        Missing packageId/instanceId.
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0 }}>
      <ColoradoFormPdfEditor
        doc={{ packageId, instanceId, name }}
        getToken={getToken}
        onClose={() => notifyClosed(false)}
        onSaved={() => notifyClosed(true)}
      />
    </div>
  );
}
